import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { pitchType, number, ToggleButtons, HomePlateChart } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';

const buildSeries = (translate, events, isgroup) => {
    const pitches = [];
    const gameids = [];
    events.forEach((v) => {
        let tooltipStr = `${translate('components.blocking-chart.wppb-probability', { value: (v.pwppbalt * 100).toFixed(1) })}<br />`;
        tooltipStr += `${pitchType(v.pitchtype, true, undefined, translate)} ${translate('format.abbr-mph', { value: number(v.velocity, 0) })}<br />`;
        tooltipStr += `${translate('baseball.pitcher_one')}: ${v.pitchername}<br />`;
        tooltipStr += `${translate('baseball.batter')}: ${v.battername}<br />`;
        if (v.r1name != null) tooltipStr += `${translate('baseball.1b')} ${translate('baseball.runners', { count: 1 })}: ${v.r1name}<br />`;
        if (v.r2name != null) tooltipStr += `${translate('baseball.2b')} ${translate('baseball.runners', { count: 1 })}: ${v.r2name}<br />`;
        if (v.r3name != null) tooltipStr += `${translate('baseball.3b')} ${translate('baseball.runners', { count: 1 })}: ${v.r3name}<br />`;
        if (!isgroup || (isgroup && gameids.indexOf(v.gameid) !== -1)) {
            pitches.push({
                id: `${v.gameid}_${v.eventseq}_${v.pitchseq}`,
                eventseq: v.eventseq,
                gameid: v.gameid,
                name: translate('components.blocking-chart.pitch-in-dirt'),
                pitchseq: v.pitchseq,
                adv: v.advr,
                tooltip: tooltipStr,
                x: v.pfx_dirt_posx,
                y: v.pfx_dirt_posy,
                batter: v.batter,
                pitcher: v.pitcher,
                inning: v.inning,
                outsbefore: v.outsbefore,
                balls: v.balls,
                strikes: v.strikes,
                islhb: v.islhb,
                url: v.url,
                url_angleb: v.url_angleb,
                url_anglec: v.url_anglec,
                url_angled: v.url_angled,
                url_anglee: v.url_anglee,
                url_anglef: v.url_anglef,
                url_angleg: v.url_angleg,
                url_angleh: v.url_angleh,
                url_anglei: v.url_anglei,
                url_anglej: v.url_anglej,
                url_anglek: v.url_anglek,
                url_anglel: v.url_anglel,
                url_anglem: v.url_anglem,
                url_anglen: v.url_anglen,
                url_angleo: v.url_angleo,
                url_anglep: v.url_anglep,
                url_angleq: v.url_angleq,
                url_angler: v.url_angler,
                url_angles: v.url_angles,
                url_anglet: v.url_anglet,
                url_angleu: v.url_angleu,
                url_anglev: v.url_anglev,
                url_anglew: v.url_anglew,
                url_anglex: v.url_anglex,
                url_angley: v.url_angley,
                url_anglez: v.url_anglez,
                url_hd: v.url_hd,
                url_sd: v.url_sd,
                view_angleb: v.view_angleb,
                view_anglec: v.view_anglec,
                view_angled: v.view_angled,
                view_anglee: v.view_anglee,
                view_anglef: v.view_anglef,
                view_angleg: v.view_angleg,
                view_angleh: v.view_angleh,
                view_anglei: v.view_anglei,
                view_anglej: v.view_anglej,
                view_anglek: v.view_anglek,
                view_anglel: v.view_anglel,
                view_anglem: v.view_anglem,
                view_anglen: v.view_anglen,
                view_angleo: v.view_angleo,
                view_anglep: v.view_anglep,
                view_angleq: v.view_angleq,
                view_angler: v.view_angler,
                view_angles: v.view_angles,
                view_anglet: v.view_anglet,
                view_angleu: v.view_angleu,
                view_anglev: v.view_anglev,
                view_anglew: v.view_anglew,
                view_anglex: v.view_anglex,
                view_angley: v.view_angley,
                view_anglez: v.view_anglez,
            });
        }
    });
    return [{
        data: pitches.filter((x) => x.adv === 0),
        id: 'noadv',
        index: 1,
        name: translate('components.blocking-chart.runner-not-advanced'),
        visible: true,
        marker: { fillColor: '#00ff00', lineColor: 'rgba(0,0,0,0.5)', lineWidth: 1, radius: 2, symbol: 'circle' },
    }, {
        data: pitches.filter((x) => x.adv === 1),
        id: 'adv',
        index: 1,
        name: translate('components.blocking-chart.runner-advanced'),
        visible: true,
        marker: { fillColor: '#ff0000', lineColor: 'rgba(0,0,0,0.5)', lineWidth: 1, radius: 4, symbol: 'circle' },
    }];
};

const VIEW = { ALL: 'all', LHH: 'lhh', RHH: 'rhh' };

const BlockingChart = ({ events, isGroup, onClick }) => {
    const { t: translate } = useTranslation();
    const [view, setView] = useState(VIEW.ALL);
    const options = [
        { name: translate('common.all'), value: VIEW.ALL, variant: 'default' },
        { name: translate('baseball.lhh'), value: VIEW.LHH, variant: 'default' },
        { name: translate('baseball.rhh'), value: VIEW.RHH, variant: 'default' },
    ];

    const filteredEvents = events.filter((pb) => (view === VIEW.LHH && pb.islhb) || (view === VIEW.RHH && !pb.islhb) || view === VIEW.ALL);
    const seriesData = buildSeries(translate, filteredEvents, isGroup);

    return (
        <>
            <ToggleButtons name="blockingchart" size="sm" options={options} onChange={(value) => setView(value)} defaultValue={VIEW.ALL} />
            <HomePlateChart legendPosition="upperleft" onClick={(p) => onClick(p)} seriesData={seriesData} turboThreshold={1250} />
        </>
    );
};

BlockingChart.propTypes = {
    events: PropTypes.arrayOf(PropTypes.object).isRequired,
    isGroup: PropTypes.bool,
    onClick: PropTypes.func,
};

BlockingChart.defaultProps = {
    isGroup: false,
    onClick: undefined,
};

export default BlockingChart;
